import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpRight, Check, ChevronDown, ChevronUp, Eye, FileText, Languages, Link2, Loader2, MessageSquare, MoreHorizontal, Pencil, Send, Share2, ThumbsUp, Trash2, Volume2, VolumeX, Wand2, WifiOff, X, Flag } from "lucide-react";
import axios from "axios";
import * as Select from "@radix-ui/react-select";
import { motion, AnimatePresence } from "motion/react";
import { createPortal } from "react-dom";

import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select as UiSelect, SelectContent as UiSelectContent, SelectItem as UiSelectItem, SelectTrigger as UiSelectTrigger, SelectValue as UiSelectValue } from "../ui/select";
import { badgeVariants } from "../ui/badge";
import { cn } from "../ui/utils";
import { ImpressiveTooltip } from "../ui/impressive-tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";
import { ArticleListenButton } from "./ArticleListenButton";

import { showErrorToast, showInfoToast, showSuccessToast } from "../../lib/toast";
import { copyToClipboard } from "../../lib/clipboard";
import { shareLink } from "../../lib/share";
import { ReportDialog, ReportTarget } from "./ReportDialog";
import { withLanguageParam } from "../../lib/url-state";
import { translateText } from "../../lib/translate";
import { sourceLanguageOf, speechLocaleFor } from "../../lib/language";
import { LanguagePicker } from "./LanguagePicker";
import { speakText, cancelSpeech } from "../../lib/speech";
import { domainFromUrl, normalizePointsCount, timeAgo } from "../../lib/format";
import { simulateDelay } from "../../lib/async-utils";
import { validateUrl } from "../../lib/validation";
import { slugify, slugifyAuthor } from "../../lib/slug";
import ApiService from "../../services/api-service";
import { TYPE_META, FIELD_LAYOUT, FieldConfig, FieldKey, normalizeAiPromptComplexity, AI_PROMPT_COMPLEXITY_META } from "../../constants/feed-meta";
import { QUOTE_LANGUAGES, DEFAULT_QUOTE_LANGUAGE } from "../../constants/languages";
import { FeedItem, TagMeta, Vote } from "../../types/feed";
import { CurrentUser } from "../../types/user";
import { API_BASE_URL, PUBLIC_SITE_URL } from "../../constants/app";
import { resolveVoteAction, VoteState } from "../../lib/vote-logic";
import { Link } from "react-router-dom";
import { isNativeApp } from "../../lib/native";

// interfaces and types for props and state management for the FeedList component
const COMMUNITY_TAG_LIST_CLASS =
  "community-scrollbar flex max-h-[7.5rem] snap-y snap-proximity scroll-smooth flex-col items-start gap-1 overflow-y-auto overscroll-contain pr-1.5";

interface CommunityTagListProps {
  tags: TagMeta[];
  isMine: (meta: TagMeta) => boolean;
  onRemove: (name: string) => void;
}

// Community tags in a short, scrollable list: a fade and a bouncing "scroll
// for more" cue appear only while there are tags below the visible rows.
const CommunityTagList = ({ tags, isMine, onRemove }: CommunityTagListProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(false);

  const updateHasMore = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setHasMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  }, []);

  useEffect(() => {
    updateHasMore();
  }, [tags.length, updateHasMore]);

  return (
    <div className="relative">
      <div ref={listRef} onScroll={updateHasMore} className={COMMUNITY_TAG_LIST_CLASS}>
        {tags.map((meta, index) => {
          const mine = isMine(meta);
          return (
            <span
              key={`${meta.name}-${meta.user_id}-${index}`}
              title={mine ? "Added by you" : `Added by ${meta.username}`}
              className={`snap-start inline-flex max-w-full items-center gap-1 rounded-full px-1.5 py-px text-[11px] font-medium transition-transform duration-200 hover:translate-x-0.5 ${
                mine
                  ? "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300"
                  : "bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300"
              }`}
            >
              <Link to={`/tags/${slugify(meta.name)}`} className="truncate hover:underline">
                #{meta.name}
              </Link>
              {mine && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(meta.name);
                  }}
                  className="shrink-0 opacity-60 hover:opacity-100"
                  aria-label={`Remove tag ${meta.name}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      {hasMore && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center rounded-b-md bg-gradient-to-t from-popover via-popover/80 to-transparent pb-0.5 pt-7">
          <span className="flex items-center gap-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">
            More below
            <ChevronDown className="h-3 w-3 animate-bounce" aria-hidden="true" />
          </span>
        </div>
      )}
    </div>
  );
};

interface FeedListProps {
  items: FeedItem[];
  categories: any[];
  currentUser?: CurrentUser | null;
  isLoading: boolean;
  onVote: (id: number, delta: number, updatedVotes?: Vote[], postType?: string, absolutePoints?: number, updatedTimestamp?: string | number) => void;
  onAddComment: (itemId: number, postType: string, comment: any) => Promise<void>;
  onDeleteComment: (itemId: number, commentId: number) => void;
  onEditPost: (item: FeedItem | number, updateData: any) => void;
  onDeletePost: (id: number, postType: string) => Promise<void> | void;
  onRequireAuth: () => void;
  emptyState?: React.ReactNode;
  // The last load failed (offline, server error): shows a retry panel instead
  // of "No content found", which would wrongly suggest the feed is empty.
  loadError?: boolean;
  // Whether the device had no connection when the load failed (picks the wording).
  offline?: boolean;
  onRetry?: () => void;
  loadingPlaceholderCount?: number;
  // Hides the per-card author byline — for pages (like a user profile) where
  // every card already belongs to the same person shown once at the top.
  hideAuthor?: boolean;
  // When set (to a QUOTE_LANGUAGES code), every quote card switches to this
  // language - the "Translate Quotes" button's bulk action. Cards still
  // allow switching individually afterward via their own language menu.
  globalQuoteLanguage?: string;
}

// sub-components props interfaces
interface VoteColumnProps {
  points: number;
  liked: boolean;
  onToggle: () => void;
}
// Edit Post Modal Props

interface EditPostModalProps {
  item: FeedItem;
  open: boolean;
  categories: any[];
  onClose: () => void;
  onSubmit: (item: FeedItem, updateData: any) => void;
  onDelete: (itemId: number, postType: string) => Promise<void> | void;
}
// Item Card Props
interface ItemCardProps {
  item: FeedItem;
  categories: any[];
  onVote: FeedListProps["onVote"];
  onAddComment: FeedListProps["onAddComment"];
  onDeleteComment: FeedListProps["onDeleteComment"];
  onEditPost: FeedListProps["onEditPost"];
  onDeletePost: FeedListProps["onDeletePost"];
  onRequireAuth: FeedListProps["onRequireAuth"];
  currentUser?: CurrentUser | null;
  index?: number;
  hideAuthor?: boolean;
  globalQuoteLanguage?: FeedListProps["globalQuoteLanguage"];
}
// Impressive Select Option Props
interface ImpressiveSelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}
// Impressive Select Props
interface ImpressiveSelectProps {
  id: string;
  label?: string;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  options: ImpressiveSelectOption[];
  onChange: (value: string) => void;
}

// SVG Icons for ChatGPT and Perplexity
const ChatGPTLogo = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 512 509.639" {...props}>
    <path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z" />
    <path
      fillRule="nonzero"
      d="M412.037 221.764a90.834 90.834 0 0 0 4.648-28.67 90.79 90.79 0 0 0-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 0 0-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 0 0-12.407 46.077 91.833 91.833 0 0 0 23.694 61.553 90.802 90.802 0 0 0-4.649 28.67 90.804 90.804 0 0 0 12.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0 0 19.253-2.04 90.783 90.783 0 0 0 67.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0 0 12.383-46.035 91.83 91.83 0 0 0-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 0 1-43.611-15.8 56.936 56.936 0 0 0 2.155-1.221l72.54-41.901a11.799 11.799 0 0 0 5.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 0 1-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0 0 11.918-.002l88.563-51.137v35.408a1.1 1.1 0 0 1-.438.94l-73.33 42.339a68.43 68.43 0 0 1-34.11 9.12 68.359 68.359 0 0 1-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0 1 35.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 0 0 5.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 0 1-1.034.093l-73.337-42.375a68.36 68.36 0 0 1-34.095-59.143 68.412 68.412 0 0 1 9.112-34.085l-.004-.003zm251.907 58.621-88.563-51.137 30.661-17.697a1.097 1.097 0 0 1 1.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 0 0-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 0 1 .44-.88l73.33-42.303a68.301 68.301 0 0 1 34.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 0 1-.984 11.545v.005zm-191.843 63.109-30.668-17.704a1.09 1.09 0 0 1-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0 1 43.689 15.804 63.09 63.09 0 0 0-2.155 1.222l-72.54 41.9a11.794 11.794 0 0 0-5.961 10.248v.068l-.05 102.23zm16.655-35.91 39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"
    />
  </svg>
);

const ClaudeLogo = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 512 509.64" {...props}>
    <path fill="#D77655" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.612-115.613 115.612H115.612C52.026 509.639 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z" />
    <path fill="#FCF2EE" fillRule="nonzero" d="M142.27 316.619l73.655-41.326 1.238-3.589-1.238-1.996-3.589-.001-12.31-.759-42.084-1.138-36.498-1.516-35.361-1.896-8.897-1.895-8.34-10.995.859-5.484 7.482-5.03 10.717.935 23.683 1.617 35.537 2.452 25.782 1.517 38.193 3.968h6.064l.86-2.451-2.073-1.517-1.618-1.517-36.776-24.922-39.81-26.338-20.852-15.166-11.273-7.683-5.687-7.204-2.451-15.721 10.237-11.273 13.75.935 3.513.936 13.928 10.716 29.749 23.027 38.848 28.612 5.687 4.727 2.275-1.617.278-1.138-2.553-4.271-21.13-38.193-22.546-38.848-10.035-16.101-2.654-9.655c-.935-3.968-1.617-7.304-1.617-11.374l11.652-15.823 6.445-2.073 15.545 2.073 6.547 5.687 9.655 22.092 15.646 34.78 24.265 47.291 7.103 14.028 3.791 12.992 1.416 3.968 2.449-.001v-2.275l1.997-26.641 3.69-32.707 3.589-42.084 1.239-11.854 5.863-14.206 11.652-7.683 9.099 4.348 7.482 10.716-1.036 6.926-4.449 28.915-8.72 45.294-5.687 30.331h3.313l3.792-3.791 15.342-20.372 25.782-32.227 11.374-12.789 13.27-14.129 8.517-6.724 16.1-.001 11.854 17.617-5.307 18.199-16.581 21.029-13.75 17.819-19.716 26.54-12.309 21.231 1.138 1.694 2.932-.278 44.536-9.479 24.062-4.347 28.714-4.928 12.992 6.066 1.416 6.167-5.106 12.613-30.71 7.583-36.018 7.204-53.636 12.689-.657.48.758.935 24.164 2.275 10.337.556h25.301l47.114 3.514 12.309 8.139 7.381 9.959-1.238 7.583-18.957 9.655-25.579-6.066-59.702-14.205-20.474-5.106-2.83-.001v1.694l17.061 16.682 31.266 28.233 39.152 36.397 1.997 8.999-5.03 7.102-5.307-.758-34.401-25.883-13.27-11.651-30.053-25.302-1.996-.001v2.654l6.926 10.136 36.574 54.975 1.895 16.859-2.653 5.485-9.479 3.311-10.414-1.895-21.408-30.054-22.092-33.844-17.819-30.331-2.173 1.238-10.515 113.261-4.929 5.788-11.374 4.348-9.478-7.204-5.03-11.652 5.03-23.027 6.066-30.052 4.928-23.886 4.449-29.674 2.654-9.858-.177-.657-2.173.278-22.37 30.71-34.021 45.977-26.919 28.815-6.445 2.553-11.173-5.789 1.037-10.337 6.243-9.2 37.257-47.392 22.47-29.371 14.508-16.961-.101-2.451h-.859l-98.954 64.251-17.618 2.275-7.583-7.103.936-11.652 3.589-3.791 29.749-20.474-.101.102.024.101z" />
  </svg>
);

interface IconProps extends React.SVGProps<SVGSVGElement> {}

const PerplexityIcon: React.FC<IconProps> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision" textRendering="geometricPrecision" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 512 509.64" {...props}>
    <path fill="#1F1F1F" d="M115.613 0h280.774C459.974 0 512 52.025 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.613C52.026 509.64 0 457.614 0 394.027V115.612C0 52.025 52.026 0 115.613 0z" />
    <path
      fill="#fff"
      fillRule="nonzero"
      d="M348.851 128.063l-68.946 58.302h68.946v-58.302zm-83.908 48.709l100.931-85.349v94.942h32.244v143.421h-38.731v90.004l-94.442-86.662v83.946h-17.023v-83.906l-96.596 86.246v-89.628h-37.445V186.365h38.732V90.768l95.309 84.958v-83.16h17.023l-.002 84.206zm-29.209 26.616c-34.955.02-69.893 0-104.83 0v109.375h20.415v-27.121l84.415-82.254zm41.445 0l82.208 82.324v27.051h21.708V203.388c-34.617 0-69.274.02-103.916 0zm-42.874-17.023l-64.669-57.646v57.646h64.669zm13.617 124.076v-95.2l-79.573 77.516v88.731l79.573-71.047zm17.252-95.022v94.863l77.19 70.83c0-29.485-.012-58.943-.012-88.425l-77.178-77.268z"
    />
  </svg>
);
// Skeleton Card Component
const SkeletonCard = () => (
  <Card className="w-full border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
    <CardContent className="p-4">
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-6 rounded bg-slate-200 animate-pulse" />
          <div className="w-6 h-4 bg-slate-200 animate-pulse" />
          <div className="w-10 h-6 rounded bg-slate-200 animate-pulse" />
        </div>
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-16 h-5 rounded bg-slate-200 animate-pulse" />
            <div className="w-12 h-5 rounded bg-slate-200 animate-pulse" />
            <div className="w-20 h-3 rounded bg-slate-200 animate-pulse" />
          </div>
          <div className="w-full h-6 rounded bg-slate-200 animate-pulse" />
          <div className="w-4/5 h-3 rounded bg-slate-200 animate-pulse" />
          <div className="w-3/5 h-3 rounded bg-slate-200 animate-pulse" />
          <div className="flex items-center gap-3 pt-2">
            <div className="w-16 h-5 rounded bg-slate-200 animate-pulse" />
            <div className="w-12 h-5 rounded bg-slate-200 animate-pulse" />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);
// One button in the app's card footer: icon + short label/count in a
// rounded pill, with a 40px+ touch target.
const NATIVE_ACTION_CLASS =
  "inline-flex h-10 min-w-10 shrink-0 items-center justify-center gap-1.5 rounded-full px-3 text-[13px] font-medium text-slate-600 transition-colors active:bg-slate-100 disabled:opacity-60 dark:text-slate-300 dark:active:bg-slate-800";
const NATIVE_MENU_ITEM_CLASS = "min-h-11 rounded-lg focus:bg-slate-100 dark:focus:bg-slate-700";

// Vote Column Component
const VoteColumn = ({ points, liked, onToggle }: VoteColumnProps) => {
  const label = liked ? "Thumbs-up registered" : "Give a thumbs-up";
  const description = liked
    ? "You already showed support for this post."
    : "Let others know this resonated with you.";
  const containerAccent = liked
    ? "border-orange-200 bg-gradient-to-b from-orange-50 via-orange-100 to-orange-200 dark:from-orange-900 dark:via-orange-700 dark:to-orange-800"
    : "border-transparent bg-white/80 dark:bg-slate-900/70";

  return (
    <motion.div
      className={`flex flex-col items-center gap-3 min-w-[50px] rounded-xl border ${containerAccent} px-2 py-2 shadow-sm transition-all duration-200 hover:shadow-lg`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <ImpressiveTooltip label={label} description={description} icon={<ThumbsUp className="w-3.5 h-3.5" />} side="right">
          <Button
            variant={liked ? "default" : "outline"}
            size="sm"
            className={`w-10 h-8 rounded transition-all duration-200 ${
              liked
            ? "bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
            : "hover:bg-slate-100 hover:border-slate-400 text-slate-600 border-slate-300 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:text-slate-400 dark:border-slate-600"
            }`}
            onClick={onToggle}
            aria-label={label}
          >
            <ThumbsUp className="w-4 h-4" />
          </Button>
        </ImpressiveTooltip>
      </motion.div>

      <motion.div
        className="text-sm font-semibold tabular-nums text-center text-slate-700 dark:text-slate-300"
        key={points}
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        {points.toLocaleString()}
      </motion.div>
    </motion.div>
  );
};
// Impressive Select Component
const ImpressiveSelect = ({ id, label, value, placeholder = "Select an option", disabled = false, options, onChange }: ImpressiveSelectProps) => {
  const hasValue = value && options.some((opt) => opt.value === value);
  const normalizedValue = hasValue ? value : undefined;
  const activeOption = hasValue ? options.find((opt) => opt.value === value) : undefined;

  return (
    <div className="space-y-1">
      {label && (
        <Label htmlFor={id} className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          {label}
        </Label>
      )}

      <Select.Root value={normalizedValue} onValueChange={onChange} disabled={disabled}>
        <Select.Trigger
          id={id}
          className="group w-full h-14 rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-r from-white via-slate-50 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 px-4 py-2 shadow-sm hover:shadow-md transition-shadow duration-200 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 flex items-center gap-3 text-left"
        >
          <span
            className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-200 ${
              activeOption ? "group-hover:bg-orange-100 group-hover:text-orange-600" : "opacity-80"
            }`}
          >
            {activeOption?.icon || <Wand2 className="w-5 h-5" />}
          </span>

          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{activeOption?.label || placeholder}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
              {activeOption?.description || "Pick the vibe that best fits your post"}
            </p>
          </div>

          <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
            <Wand2 className="w-4 h-4 hidden sm:inline-flex" />
            <ChevronDown className="w-4 h-4" />
          </div>

          <Select.Value className="sr-only" placeholder={placeholder} />
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            align="start"
            className="z-50 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl min-w-[260px] overflow-hidden"
          >
            <Select.ScrollUpButton className="flex items-center justify-center py-2 text-slate-400">
              <ChevronUp className="w-4 h-4" />
            </Select.ScrollUpButton>
            <Select.Viewport className="p-2 max-h-64">
              {options.map((opt) => (
                <Select.Item
                  key={opt.value}
                  value={opt.value}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer text-slate-800 dark:text-slate-100 focus:bg-orange-50 focus:text-orange-900 dark:focus:bg-slate-800 dark:focus:text-slate-100 data-[state=checked]:border data-[state=checked]:border-orange-200 dark:data-[state=checked]:border-slate-700"
                >
                  <span className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-base">
                    {opt.icon || <Wand2 className="w-4 h-4" />}
                  </span>
                  <div className="flex-1">
                    <Select.ItemText>
                      <span className="text-sm font-semibold">{opt.label}</span>
                    </Select.ItemText>
                    {opt.description && <p className="text-xs text-slate-500 dark:text-slate-400">{opt.description}</p>}
                  </div>
                  <Select.ItemIndicator className="ml-auto text-orange-500">
                    <Check className="w-4 h-4" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
            <Select.ScrollDownButton className="flex items-center justify-center py-2 text-slate-400">
              <ChevronDown className="w-4 h-4" />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
};
// Edit Post Modal Component
function EditPostModal({ item, open, onClose, onSubmit, onDelete, categories }: EditPostModalProps) {
  const resolveCategoryId = useCallback(
    (value?: string) => {
      if (!value) return "mindset";
      const normalized = value.toString().toLowerCase();
      const match = categories.find((cat: any) =>
        [cat.id, cat._id, cat.name]
          .filter(Boolean)
          .some((key: any) => key.toString().toLowerCase() === normalized)
      );
      const resolved = match?.id ?? match?._id ?? value;
      return resolved?.toString() || "mindset";
    },
    [categories]
  );

  const initialCategory = useMemo(() => resolveCategoryId(item?.category_id || item?.category || "mindset"), [item, resolveCategoryId]);

  const [title, setTitle] = useState(item?.title || "");
  const [content, setContent] = useState(item?.content || "");
  const [author, setAuthor] = useState(item?.author || "");
  const [url, setUrl] = useState(item?.url || "");
  const [complexity, setComplexity] = useState(normalizeAiPromptComplexity(item?.complexity));
  const [category, setCategory] = useState(initialCategory);
  // Normalize + dedupe existing tags (older posts may have both "focus" and
  // "#focus" stored as separate tags) and show them consistently with a
  // leading '#', matching how they're displayed on the card.
  const [tags, setTags] = useState(() => {
    const seen = new Set<string>();
    const cleaned: string[] = [];
    (item?.tags || []).forEach((t) => {
      const normalized = String(t).trim().toLowerCase().replace(/^#+/, "");
      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        cleaned.push(normalized);
      }
    });
    return cleaned.map((t) => `#${t}`).join(", ");
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    setCategory(initialCategory);
  }, [initialCategory]);

  const categoryOptions = useMemo(
    () =>
      categories.slice(1).map((cat: any) => ({
        value: (cat.id ?? cat._id ?? cat.name)?.toString(),
        label: cat.name || cat.title || "Category",
        description: cat.description || cat.tagline || cat.subtitle || `Highlight ${cat.name || "this theme"}`,
        icon: cat.icon,
      })),
    [categories]
  );

  const selectedCategoryOption = categoryOptions.find((opt: any) => opt.value === category);
  const normalizedType = useMemo(() => (item?.type === "aiprompt" ? "prompt" : item?.type || "default"), [item?.type]);
  const activeFields = useMemo(() => FIELD_LAYOUT[normalizedType] || FIELD_LAYOUT.default, [normalizedType]);

  const fieldBindings: Record<FieldKey, { value: string; setter: React.Dispatch<React.SetStateAction<string>> }> = {
    title: { value: title, setter: setTitle },
    content: { value: content, setter: setContent },
    author: { value: author, setter: setAuthor },
    url: { value: url, setter: setUrl },
    complexity: { value: complexity, setter: setComplexity as React.Dispatch<React.SetStateAction<string>> },
  };

  const handleFieldChange = (key: FieldKey, value: string) => {
    fieldBindings[key].setter(value);
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: null }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    activeFields.forEach((field) => {
      if (!field.required) return;
      const rawValue = (fieldBindings[field.key].value || "").trim();
      if (!rawValue) {
        newErrors[field.key] = field.label.replace("*", "").trim() + " is required";
        return;
      }
      if (field.key === "url" && !validateUrl(rawValue)) {
        newErrors.url = "Valid URL is required";
      }
    });

    return newErrors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});
    await simulateDelay(300);
    console.log("Submitting updated post:", { title, content, author, url, category, tags });

    const updatedItem = {
      title: title.trim() || content.trim(),
      content: content.trim(),
      type: item.type,
      author: author.trim(),
      url: url.trim(),
      category,
      category_id: category,
      complexity,
      // A leading '#' is just how tags are typed/displayed, not part of the
      // tag itself - strip it and dedupe so "#focus" and "focus" don't end
      // up as two different tags.
      tags: Array.from(
        new Set(
          tags
            .split(",")
            .map((t) => t.trim().toLowerCase().replace(/^#+/, ""))
            .filter(Boolean)
        )
      ),
    };

    try {
      await ApiService.updateContent(item.id, updatedItem);
      const categoryLabel = selectedCategoryOption?.label || selectedCategoryOption?.value || updatedItem.category;
      const updatedUiItem = {
        ...updatedItem,
        category: categoryLabel,
        category_name: categoryLabel,
      };
      onSubmit(item, updatedUiItem);
      onClose();
      showSuccessToast("Post updated successfully!");
    } catch (error) {
      console.error("Failed to update post:", error);
      showErrorToast("Failed to update post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePost = async () => {
    setIsLoading(true);
    try {
      await onDelete(item.id, item.type);
      setShowDeleteDialog(false);
      onClose();
      showSuccessToast("Post deleted successfully!");
    } catch (error) {
      console.error("Failed to delete post:", error);
      showErrorToast("Failed to delete post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderField = (field: FieldConfig) => {
    const binding = fieldBindings[field.key];
    if (!binding) return null;

    const inputId = `edit-${field.key}`;
    const error = errors[field.key];
    const cardClasses = "rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-2";

    return (
      <div key={field.key} className={cardClasses}>
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor={inputId} className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {field.label}
          </Label>
          {field.helper && <span className="text-xs text-slate-400 dark:text-slate-500">{field.helper}</span>}
        </div>

        {field.type === "select" ? (
          <UiSelect value={binding.value} onValueChange={(value: string) => handleFieldChange(field.key, value)}>
            <UiSelectTrigger id={inputId} className={error ? "border-red-300 focus:border-red-500" : ""}>
              <UiSelectValue placeholder={field.placeholder} />
            </UiSelectTrigger>
            <UiSelectContent>
              {(field.options || []).map((option) => (
                <UiSelectItem key={option.value} value={option.value}>
                  {option.label}
                </UiSelectItem>
              ))}
            </UiSelectContent>
          </UiSelect>
        ) : field.type === "textarea" ? (
          <Textarea
            id={inputId}
            value={binding.value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows ?? 4}
            className={error ? "border-red-300 focus:border-red-500" : ""}
          />
        ) : (
          <Input
            id={inputId}
            type={field.type === "url" ? "url" : "text"}
            value={binding.value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={error ? "border-red-300 focus:border-red-500" : ""}
          />
        )}

        {error && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <Wand2 className="w-3 h-3" />
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(value: boolean) => !value && !isLoading && onClose()}>
        <DialogContent className="flex w-full max-w-[min(100vw-1.5rem,64rem)] flex-col overflow-hidden rounded-2xl p-4 sm:p-6 max-h-[90vh] min-h-0">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
            <DialogDescription>Update your post details</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-1 min-h-0 flex-col gap-6 overflow-hidden">
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto pr-1 sm:pr-2">
              <div className="space-y-6">
                <div className={`grid gap-4 ${activeFields.length > 1 ? "lg:grid-cols-2" : ""}`}>{activeFields.map((field) => renderField(field))}</div>

                <div className="space-y-4">
                  <ImpressiveSelect
                    id="impressive-category"
                    label="Category"
                    value={category}
                    options={categoryOptions}
                    onChange={setCategory}
                    placeholder="Select a category"
                    disabled={isLoading || categoryOptions.length === 0}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="edit-tags" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Tags
                    </Label>
                    <Input
                      id="edit-tags"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="#productivity, #mindset, #habits"
                      className="text-sm"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Separate tags with commas to reach the right audience. The # is optional — with or without it works the same.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollArea>

            <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 dark:border-slate-700 sm:flex-row sm:justify-end">
              <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100 text-white dark:text-slate-900 w-full sm:w-auto"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button type="button" variant="destructive" className="bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 text-white" onClick={() => setShowDeleteDialog(true)} disabled={isLoading}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Post
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={(value: boolean) => !value && !isLoading && setShowDeleteDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Post</DialogTitle>
            <DialogDescription>Are you sure you want to delete this post? This action cannot be undone.</DialogDescription>
          </DialogHeader>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePost} disabled={isLoading} className="bg-red-600 hover:bg-red-700 text-white">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Post
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
// Item Card Component
const ItemCard = React.memo(
  ({
    item,
    categories,
    onVote,
    onAddComment,
    onDeleteComment,
    onEditPost,
    onDeletePost,
    onRequireAuth,
    currentUser,
    index = 0,
    hideAuthor = false,
    globalQuoteLanguage,
  }: ItemCardProps) => {
    const normalizedType = item.type === "aiprompt" ? "prompt" : item.type;
    const meta =
      (TYPE_META as Record<string, { icon: React.ReactNode; label: string; color: string; bg: string }>)[normalizedType] || {
        icon: null,
        label: normalizedType,
        color: "text-slate-600 dark:text-slate-400",
        bg: "bg-slate-100 dark:bg-slate-700",
      };

    const [voted, setVoted] = useState<VoteState>(0);
    const [commentsOpen, setCommentsOpen] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [showAllComments, setShowAllComments] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
    const [preview, setPreview] = useState<any>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const previewAnchorRef = useRef<HTMLDivElement>(null);
    const [previewAnchorRect, setPreviewAnchorRect] = useState<DOMRect | null>(null);
    const [showFull, setShowFull] = useState(false);
    // The language this quote was written in (quotes.language, e.g. "ta"),
    // not assumed to be English - translations are made *from* it.
    const originalText = (item.title ?? item.content ?? "").trim();
    const sourceLanguage = useMemo(
      () => sourceLanguageOf(item),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [item.language, item.title, item.content]
    );
    // Translations are cached per language + text, so switching back to one
    // already fetched doesn't re-hit the translation API, and an edited quote
    // never shows a translation of its old text.
    const [translatedTexts, setTranslatedTexts] = useState<Record<string, string>>({});
    // The translation being shown; null = the original text.
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
    // Most recently requested language, so a slow translation can't replace
    // a newer choice when it finally arrives.
    const latestLanguageRequestRef = useRef<string | null>(null);
    const [translating, setTranslating] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    // Folksonomy: lets any logged-in viewer (not just the creator) add their
    // own tag to this card - the "+" chip below expands into this input.
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [communityOpen, setCommunityOpen] = useState(false);
    const communityCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // A touch tap on the "+" only toggles the community list; adding happens from the list.
    const plusPressedByTouch = useRef(false);
    const [newTagValue, setNewTagValue] = useState("");
    const [isSubmittingTag, setIsSubmittingTag] = useState(false);

    const isOwner =
      !!currentUser?.id && item.user_id != null && Number(item.user_id) === Number(currentUser.id);
    const isAuthed = !!currentUser;
    const authorName = item.user || item.username;
    const authorLink =
      item.user_id != null ? `/user/${encodeURIComponent(authorName || "user")}/${item.user_id}` : null;
    const domain = domainFromUrl(item.url);
    // Every card shares its own page on The Deepam (/post/:type/:id), not the
    // external source URL, so each item has a link that opens it in the app.
    const shareTargetUrl = useMemo(() => `${PUBLIC_SITE_URL}/post/${item.type}/${item.id}`, [item.id, item.type]);
    const bookSummaryText = useMemo(() => {
      if (item.type !== "book") return "";
      const rawSummary = (typeof item.summary === "string" ? item.summary : "") || item.content || "";
      return rawSummary.trim();
    }, [item.content, item.summary, item.type]);

    const bookPerplexityQuery = useMemo(() => {
      if (item.type !== "book") return "";
      const baseTitle = (item.title?.trim() || "").replace(/\s+/g, " ");
      const queryTitle = baseTitle || "this book";
      const optionalUrl = item.url ? ` ${item.url}` : "";
      return `Summary of ${queryTitle} this book${optionalUrl}`;
    }, [item.title, item.url, item.type]);

    const handleBookSummaryClick = useCallback(
      (event?: React.MouseEvent) => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        if (!bookSummaryText || !bookPerplexityQuery) return;
        const encodedQuery = encodeURIComponent(bookPerplexityQuery);
        window.open(`https://www.perplexity.ai/search/new?q=${encodedQuery}`, "_blank", "noopener,noreferrer");
      },
      [bookPerplexityQuery, bookSummaryText]
    );

    const [localPoints, setLocalPoints] = useState(() => normalizePointsCount(item.points_count));

    useEffect(() => {
      setLocalPoints(normalizePointsCount(item.points_count));
    }, [item.points_count]);

    useEffect(() => {
      if (Array.isArray(item.points) && currentUser) {
        const existingVote = item.points.find((vote: Vote) => vote.user_id === currentUser.id);
        const nextVote: VoteState = existingVote?.vote_type === "up" ? 1 : 0;
        setVoted(nextVote);
      } else {
        setVoted(0);
      }
    }, [item.points, currentUser]);


    useEffect(() => {
      return () => cancelSpeech();
    }, []);

    // Optimistically adjust the local vote state, sync with the API, and roll back if the request fails.
    // const handleVote = useCallback(
    //   async (dir: number) => {
    //     console.log("Voting", { dir, itemId: item.id, itemType: item.type, currentVotedState: voted });
    //     if (!isAuthed) {
    //       onRequireAuth?.();
    //       return;
    //     }

    //     const voteType = dir === 1 ? "upvote" : "downvote";
    //     let newVotedState: number;
    //     let pointsDelta: number;

    //     if (dir === 1) {
    //       if (voted === 1) {
    //         newVotedState = 0;
    //         pointsDelta = -1;
    //       } else {
    //         newVotedState = 1;
    //         pointsDelta = 1;
    //       }
    //     } else {
    //       if (voted === -1) {
    //         newVotedState = 0;
    //         pointsDelta = 1;
    //       } else if (voted === 1) {
    //         newVotedState = -1;
    //         pointsDelta = -2;
    //       } else {
    //         newVotedState = -1;
    //         pointsDelta = -1;
    //       }
    //     }

    //     const previousVoted = voted;
    //     const optimisticDelta = pointsDelta;
    //     const previousLocalPoints = localPoints;
    //     setLocalPoints((prev) => prev + optimisticDelta);
    //     setVoted(newVotedState);
    //     onVote(item.id, optimisticDelta, undefined, item.type);

    //     try {
    //       const response = await ApiService.vote(item.id, item.type, voteType, currentUser!.id as number);
    //       let updatedVotes: Vote[] | undefined;
    //       let authoritativePoints: number | undefined;
    //       let updatedTimestamp: string | number | undefined;

    //       const votesPayload = Array.isArray(response) ? response : Array.isArray(response?.votes) ? response.votes : undefined;
    //       if (votesPayload) {
    //         updatedVotes = votesPayload;
    //       }

    //       const pointsValueRaw = Array.isArray(response) ? undefined : response?.points_count;
    //       if (typeof pointsValueRaw === "number") {
    //         authoritativePoints = pointsValueRaw;
    //       } else if (typeof pointsValueRaw === "string") {
    //         const parsed = parseInt(pointsValueRaw, 10);
    //         if (!Number.isNaN(parsed)) {
    //           authoritativePoints = parsed;
    //         }
    //       }

    //       if (response && typeof response === "object" && !Array.isArray(response)) {
    //         updatedTimestamp = response?.updated_at || response?.updatedAt || updatedTimestamp;
    //       }

    //       if (authoritativePoints !== undefined || updatedVotes || updatedTimestamp) {
    //         onVote(item.id, 0, updatedVotes, item.type, authoritativePoints, updatedTimestamp);
    //       }

    //       if (newVotedState !== 0) {
    //         showInfoToast("Vote recorded successfully!");
    //       }
    //     } catch (error) {
    //       console.error("Failed to vote:", error);
    //       showErrorToast("Failed to record vote. Please try again.");
    //       setVoted(previousVoted);
    //       setLocalPoints(previousLocalPoints);
    //       onVote(item.id, -optimisticDelta, undefined, item.type);
    //     }
    //   },
    //   [isAuthed, currentUser, item, onRequireAuth, onVote, voted]
    // );





    const handleVote = useCallback(
      async () => {



        if (!isAuthed) {
          console.log("❌ User not authenticated. Triggering auth modal...");
          onRequireAuth?.();
          return;
        }

        const { nextState: newVotedState, delta: pointsDelta } = resolveVoteAction(voted);

        console.log("🔄 Computed like change:", {
          previousVoted: voted,
          newVotedState,
          optimisticPointsDelta: pointsDelta,
        });

        const previousVoted = voted;
        const previousLocalPoints = localPoints;

        setLocalPoints((prev) => {
          const updated = prev + pointsDelta;
          console.log("⚡ Optimistic points update:", { before: prev, after: updated });
          return updated;
        });

        setVoted(newVotedState);
        console.log("⚡ Optimistic like state update:", { after: newVotedState });

        onVote(item.id, pointsDelta, undefined, item.type);

        try {
          console.log("🌐 Sending like API request...");
          const response = await ApiService.vote(item.id, item.type, "upvote", currentUser!.id as number);

          console.log("✅ API Response received:", response);

          let updatedVotes: Vote[] | undefined;
          let authoritativePoints: number | undefined;
          let updatedTimestamp: string | number | undefined;

          // Votes
          const votesPayload = Array.isArray(response)
            ? response
            : Array.isArray(response?.votes)
            ? response.votes
            : undefined;

          if (votesPayload) {
            updatedVotes = votesPayload;
            console.log("🗳 Updated votes from server:", updatedVotes);
          }

          // Points
          const pointsValueRaw =
            Array.isArray(response) ? undefined : response?.points_count;

          if (typeof pointsValueRaw === "number") {
            console.log("🔢 Raw points value from server:", pointsValueRaw);
            authoritativePoints = pointsValueRaw;
          } else if (typeof pointsValueRaw === "string") {
            console.log("🔢 Raw points value from server1:", pointsValueRaw);
            const parsed = parseInt(pointsValueRaw, 10);
            console.log("🔢 Parsed points value:", parsed);
            if (!Number.isNaN(parsed)) authoritativePoints = parsed;
          }

          if (authoritativePoints !== undefined) {
            console.log("🔢 Authoritative points from server:", authoritativePoints);
          }

          // Timestamp
          if (typeof response === "object" && !Array.isArray(response)) {
            updatedTimestamp =
              response?.updated_at ||
              response?.updatedAt ||
              updatedTimestamp;

            console.log("⏳ Updated timestamp from server:", updatedTimestamp);
          }

          // Final sync
          if (authoritativePoints !== undefined || updatedVotes || updatedTimestamp) {
            console.log("🔄 Syncing authoritative data with parent...");
            onVote(
              item.id,
              0,
              updatedVotes,
              item.type,
              authoritativePoints,
              updatedTimestamp
            );
          }

          if (newVotedState === 1) {
            showInfoToast("Like recorded successfully!");
          }
        } catch (error) {
          console.error("❌ Failed to like:", error);

          showErrorToast("Failed to record like. Please try again.");

          console.log("↩️ Rolling back like/points changes...");
          setVoted(previousVoted);
          setLocalPoints(previousLocalPoints);

          onVote(item.id, -pointsDelta, undefined, item.type);
        }
      },
      [isAuthed, currentUser, item, onRequireAuth, onVote, voted, localPoints]
    );


    // Copies the full link to this item's page (/post/:type/:id) with the
    // language this card is currently shown in (?lang=).
    const handleCopyUrl = async () => {
      const result = await copyToClipboard(withLanguageParam(shareTargetUrl, activeLanguageCode));
      if (result.success) {
        showSuccessToast("Link copied to clipboard!");
      } else {
        showInfoToast("Unable to copy URL. Please copy manually.");
      }
    };

    // The app's Share opens the system share sheet (the website's copies the link).
    const handleShareItem = async () => {
      const url = withLanguageParam(shareTargetUrl, activeLanguageCode);
      const outcome = await shareLink({ title: "Shared from The Deepam", url });
      if (outcome === "copied") {
        showSuccessToast("Link copied to clipboard!");
      } else if (outcome === "failed") {
        showInfoToast("Unable to copy URL. Please copy manually.");
      }
    };

    const handleCopyText = async () => {
      const validTypes = ["quote", "book", "aiprompt"];
      const textToCopy = validTypes.includes(item.type) ? item.title ?? item.content ?? "" : "";
      const result = await copyToClipboard(textToCopy);

      if (result?.success) {
        showSuccessToast("Text copied to clipboard!");
      } else {
        showInfoToast("Unable to copy text. Please copy manually.");
      }
    };

    const translationKey = (languageCode: string) => `${languageCode}|${originalText}`;

    const handleLanguageChange = async (languageCode: string) => {
      // Validation: the quote is already in this language, so show the
      // original text untouched - no translation call (translating Tamil
      // "from English" is what garbled Tamil quotes).
      if (languageCode === sourceLanguage) {
        latestLanguageRequestRef.current = null;
        setSelectedLanguage(null);
        return;
      }

      latestLanguageRequestRef.current = languageCode;
      if (translatedTexts[translationKey(languageCode)]) {
        setSelectedLanguage(languageCode);
        return;
      }
      if (!originalText) return;

      setTranslating(true);
      try {
        const translated = await translateText(originalText, languageCode, sourceLanguage);
        setTranslatedTexts((prev) => ({ ...prev, [translationKey(languageCode)]: translated }));
        if (latestLanguageRequestRef.current === languageCode) setSelectedLanguage(languageCode);
      } catch (error) {
        console.error("Failed to translate quote:", error);
        if (latestLanguageRequestRef.current === languageCode) {
          latestLanguageRequestRef.current = null;
          setSelectedLanguage(null);
        }
        const message = error instanceof Error ? error.message : "Unable to translate quote. Please try again.";
        showErrorToast(message);
      } finally {
        setTranslating(false);
      }
    };

    // Follows the page's ?lang= (the "Translate Quotes" button). Without one,
    // the quote shows in its own language; with one, it's translated unless
    // it's already in that language. Re-runs if the quote is edited.
    useEffect(() => {
      if (item.type !== "quote") return;
      handleLanguageChange(globalQuoteLanguage ?? sourceLanguage);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalQuoteLanguage, sourceLanguage, originalText]);

    const activeTranslation = selectedLanguage ? translatedTexts[translationKey(selectedLanguage)] : undefined;
    // The language of the text actually on screen, so the label always
    // matches it (a Tamil quote reads "Tamil", not "English").
    const activeLanguageCode = activeTranslation && selectedLanguage ? selectedLanguage : sourceLanguage;
    const activeLanguage = QUOTE_LANGUAGES.find((lang) => lang.code === activeLanguageCode) ?? QUOTE_LANGUAGES[0];

    const handleReadAloud = () => {
      const textToRead = activeTranslation || originalText;
      if (!textToRead) return;

      if (isSpeaking) {
        cancelSpeech();
        setIsSpeaking(false);
        return;
      }

      speakText(textToRead, speechLocaleFor(activeLanguageCode), () => setIsSpeaking(false));
      setIsSpeaking(true);
    };

    const handleAddTag = async () => {
      if (!isAuthed) {
        onRequireAuth?.();
        return;
      }
      const tagToAdd = newTagValue.trim();
      if (!tagToAdd) {
        setIsAddingTag(false);
        return;
      }

      const normalizedTag = tagToAdd.toLowerCase().replace(/^#+/, "");
      const alreadyOnCard = [...(Array.isArray(item.tags) ? item.tags : []), ...folksonomyTags.map((meta) => meta.name)].some(
        (name) => name.toLowerCase().replace(/^#+/, "") === normalizedTag
      );
      if (alreadyOnCard) {
        showInfoToast(`"#${normalizedTag}" already exists on this post.`);
        setNewTagValue("");
        setIsAddingTag(false);
        return;
      }

      setIsSubmittingTag(true);
      try {
        const response = await ApiService.addTag(item.id, item.type, tagToAdd);
        if (response?.post) {
          onEditPost(item, { tags: response.post.tags, folksonomy_tags: response.post.folksonomy_tags });
        }
        setNewTagValue("");
        setIsAddingTag(false);
      } catch (error) {
        const message = error instanceof Error ? error.message : "";
        if (message.includes("already exists")) {
          showInfoToast(message);
          setNewTagValue("");
          setIsAddingTag(false);
        } else {
          console.error("Failed to add tag:", error);
          showErrorToast("Unable to add tag. Please try again.");
        }
      } finally {
        setIsSubmittingTag(false);
      }
    };

    // Community (folksonomy) tags on this card, each with who added it.
    const folksonomyTags = Array.isArray(item.folksonomy_tags) ? item.folksonomy_tags : [];
    // True for a community tag the current viewer added - the only ones they can remove.
    const isMyFolksonomyTag = (meta: { user_id: number }) =>
      !!currentUser?.id && Number(meta.user_id) === Number(currentUser.id);

    // Mouse users get the list on hover; the short close delay lets the pointer
    // travel from the "+" into the list without it closing.
    const cancelCommunityClose = () => {
      if (communityCloseTimer.current) clearTimeout(communityCloseTimer.current);
      communityCloseTimer.current = null;
    };
    const openCommunityOnHover = () => {
      cancelCommunityClose();
      setCommunityOpen(true);
    };
    const scheduleCommunityClose = () => {
      cancelCommunityClose();
      communityCloseTimer.current = setTimeout(() => setCommunityOpen(false), 150);
    };

    const handleRemoveTag = async (tagName: string) => {
      try {
        const response = await ApiService.removeTag(item.id, item.type, tagName);
        if (response?.post) {
          onEditPost(item, { tags: response.post.tags, folksonomy_tags: response.post.folksonomy_tags });
        }
      } catch (error) {
        console.error("Failed to remove tag:", error);
        showErrorToast("Unable to remove tag. Please try again.");
      }
    };

    // Validate authentication + input before delegating to the parent handler and clearing the form.
    const handleAddComment = useCallback(async () => {
      if (!isAuthed) {
        onRequireAuth?.();
        return;
      }
      const text = commentText.trim();
      if (!text) return;

      await onAddComment(item.id, item.type, {
        id: Date.now(),
        user: currentUser!.firstName,
        userId: currentUser!.id,
        user_id: currentUser!.id,
        text,
        created_at: Date.now(),
        createdBy: currentUser!.username,
      });
      setCommentText("");
      showSuccessToast("Comment added!");
    }, [commentText, isAuthed, currentUser, item, onAddComment, onRequireAuth]);

    const handleDeleteComment = useCallback(
      async (commentId: number) => {
        if (!isAuthed) {
          onRequireAuth?.();
          return;
        }
        try {
          const response = await ApiService.deleteComment(item.id, commentId, currentUser!.id as number);
          if (response) {
            onDeleteComment(item.id, commentId);
            showSuccessToast("Comment deleted!");
          }
        } catch (error) {
          console.error("Failed to delete comment:", error);
          showErrorToast("Failed to delete comment. Please try again.");
        }
      },
      [isAuthed, currentUser, item.id, onDeleteComment, onRequireAuth]
    );

    const openInChatGPT = async (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const content = item?.content?.trim();
      if (!content) return;
      const prompt = encodeURIComponent(content);
      const chatGPTUrl = `https://chatgpt.com/?q=${prompt}`;
      const cleanedUrl = chatGPTUrl.endsWith(".") ? chatGPTUrl.slice(0, -1) : chatGPTUrl;
      const result = await copyToClipboard(cleanedUrl);
      if (result?.success) {
        showSuccessToast("URL copied to clipboard!");
      }
      window.open(cleanedUrl, "_blank", "noopener,noreferrer");
    };

    const openInPerplexity = () => {
      if (item.content) {
        window.open(`https://www.perplexity.ai/search/new?q=${item.content}`, "_blank", "noopener,noreferrer");
      }
    };

    const openInClaude = () => {
      const content = item?.content?.trim();
      if (!content) return;
      window.open(`https://claude.ai/new?q=${encodeURIComponent(content)}`, "_blank", "noopener,noreferrer");
    };

    const commentsArray = Array.isArray(item.comments) ? item.comments : [];
    const text = activeTranslation || (item.title ?? item.content ?? "");
    const isLong = text.length > 180;
    const displayText = showFull ? text : text.slice(0, 180);

    const ogEndpoint = useMemo(() => {
      const base = API_BASE_URL?.replace(/\/$/, "") || "http://localhost:8080";
      return `${base}/og-preview`;
    }, []);

    // Fetch og-preview metadata for the link hover card, handling loading/error states locally.
    const fetchOG = useCallback(async () => {
      if (preview || previewError || previewLoading || !item?.url) return;
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const res = await axios.get(ogEndpoint, {
          params: { url: item.url },
          validateStatus: () => true,
        });

        const payload = res.data?.data || res.data;
        if (res.status >= 200 && res.status < 300 && payload) {
          setPreview(payload);
          setPreviewError(null);
        } else {
          setPreview(null);
          setPreviewError("Preview unavailable");
        }
      } catch (err) {
        setPreview(null);
        setPreviewError("Preview unavailable");
      } finally {
        setPreviewLoading(false);
      }
    }, [item?.url, ogEndpoint, preview, previewError, previewLoading]);

    const updatePreviewRect = useCallback(() => {
      if (typeof window === "undefined") return;
      const rect = previewAnchorRef.current?.getBoundingClientRect();
      if (rect) {
        setPreviewAnchorRect(rect);
      }
    }, []);

    useEffect(() => {
      if (!showPreview || typeof window === "undefined") return;
      const handler = () => updatePreviewRect();
      window.addEventListener("resize", handler);
      window.addEventListener("scroll", handler, { passive: true });
      return () => {
        window.removeEventListener("resize", handler);
        window.removeEventListener("scroll", handler);
      };
    }, [showPreview, updatePreviewRect]);

    const handlePreviewEnter = useCallback(() => {
      setShowPreview(true);
      updatePreviewRect();
      fetchOG();
    }, [fetchOG, updatePreviewRect]);

    const handlePreviewLeave = useCallback(() => {
      setShowPreview(false);
    }, []);

    const truncateText = useCallback((value?: string, limit = 80) => {
      if (!value) return "";
      return value.length > limit ? `${value.slice(0, limit - 1)}…` : value;
    }, []);

    const matchedCategory = useMemo(() => {
      const toCapitalize = (str: string | undefined | null) => {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
      };
      return categories.find((c: any) => {
        const changedCategory = toCapitalize(c._id);
        const catName = toCapitalize(item.category);
        const catDisplay = toCapitalize(item.category_name);
        return changedCategory === catName || changedCategory === catDisplay;
      });
    }, [categories, item.category, item.category_name]);

    const commentsPreview = showAllComments ? commentsArray : commentsArray.slice(0, 3);

    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }} className="w-full">
        <Card
          // className={`group relative overflow-visible border-b border-slate-200 dark:border-slate-700 bg-gradient-to-br from-white via-white to-slate-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 shadow-[0_20px_45px_rgba(15,23,42,0.15),0_6px_18px_rgba(15,23,42,0.08)] dark:shadow-[0_20px_45px_rgba(2,6,23,0.65),0_8px_20px_rgba(15,23,42,0.35)] transition-all duration-300 motion-safe:transition-transform motion-safe:hover:-translate-y-1 motion-safe:hover:scale-[1.002] ${
          //   voted === 1
          //   ? "ring-0 hover:ring hover:ring-orange-200 dark:hover:ring-orange-400 hover:border-orange-300 dark:hover:border-orange-400 hover:shadow-[0_8px_24px_rgba(15,23,42,0.12)] dark:hover:shadow-[0_8px_24px_rgba(15,23,42,0.35)]"
          //   : "hover:shadow-[0_8px_24px_rgba(15,23,42,0.12)] dark:hover:shadow-[0_8px_24px_rgba(15,23,42,0.35)]"
          // }`}
          className={`group relative overflow-visible border-b border-slate-200 dark:border-slate-700 bg-gradient-to-br from-white via-white to-slate-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 shadow-[0_20px_45px_rgba(15,23,42,0.15),0_6px_18px_rgba(15,23,42,0.08)] dark:shadow-[0_20px_45px_rgba(2,6,23,0.65),0_8px_20px_rgba(15,23,42,0.35)] transition-all duration-300 ${
            // Touch screens keep :hover after a tap, which would leave the
            // last-tapped card lifted and outlined in orange - so no hover
            // effects in the app.
            isNativeApp
            ? ""
            : "motion-safe:transition-transform motion-safe:hover:-translate-y-1 motion-safe:hover:scale-[1.002] " +
            (voted === 1
            ? "ring-0 hover:ring hover:ring-orange-200 dark:hover:ring-orange-400 hover:border-orange-300 dark:hover:border-orange-400 hover:shadow-[0_8px_24px_rgba(15,23,42,0.12)] dark:hover:shadow-[0_8px_24px_rgba(15,23,42,0.35)]"
            : "ring-0 hover:ring hover:ring-orange-200 dark:hover:ring-orange-400 hover:border-orange-300 dark:hover:border-orange-400 hover:shadow-[0_8px_24px_rgba(15,23,42,0.12)] dark:hover:shadow-[0_8px_24px_rgba(15,23,42,0.35)]")
          }`}
        >
          <CardContent className={`relative ${isNativeApp ? "px-4 pt-4 pb-2 space-y-3" : "p-4 space-y-4"}`}>
            {/* <div className="mb-3">
              <div
                className={`h-1.5 w-24 rounded-full bg-gradient-to-r ${
                  voted === 1
                    ? "from-orange-400 via-amber-500 to-pink-500"
                    : "from-slate-300 via-slate-400 to-slate-300"
                } opacity-90 transition-all duration-300`}
              />
            </div> */}
            <div className="flex  items-center gap-4">
              {/* In the app the like button moves into the action row below, so
                  the post text gets the card's full width on a phone. */}
              {!isNativeApp && <VoteColumn points={localPoints} liked={Boolean(voted)} onToggle={handleVote} />}

              <div className={`flex-1 min-w-0 ${isNativeApp ? "space-y-3" : "space-y-4"}`}>
                {isNativeApp ? (
                  // App header: type + category as one quiet line, with the
                  // less-used actions (copy, open, edit/report) in a "⋯" menu.
                  <div className="flex items-center gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                      <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 ${meta.bg} ${meta.color}`}>
                        {meta.icon}
                        {meta.label}
                      </span>
                      <span aria-hidden="true">·</span>
                      <span className="truncate capitalize">
                        {matchedCategory?.icon && <span className="mr-1">{matchedCategory.icon}</span>}
                        {(item.category || item.category_name || "Unknown")?.toString()}
                      </span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="More actions"
                          className="-my-2 -mr-2 grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-400 active:bg-slate-100 dark:active:bg-slate-800"
                        >
                          <MoreHorizontal className="h-5 w-5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 rounded-xl p-1.5 dark:bg-slate-800">
                        {["aiprompt", "quote"].includes(item.type) && (
                          <DropdownMenuItem onClick={handleCopyText} className={NATIVE_MENU_ITEM_CLASS}>
                            <FileText className="mr-2 h-4 w-4" /> Copy text
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild className={NATIVE_MENU_ITEM_CLASS}>
                          <Link to={`/post/${item.type}/${item.id}`}>
                            <ArrowUpRight className="mr-2 h-4 w-4" /> Open post
                          </Link>
                        </DropdownMenuItem>
                        {isOwner ? (
                          <DropdownMenuItem onClick={() => setEditOpen(true)} className={NATIVE_MENU_ITEM_CLASS}>
                            <Pencil className="mr-2 h-4 w-4" /> Edit
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => setReportTarget({ postType: item.type, postId: Number(item.id) })}
                            className={`${NATIVE_MENU_ITEM_CLASS} text-red-600 focus:text-red-600 dark:text-red-400`}
                          >
                            <Flag className="mr-2 h-4 w-4" /> Report
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${meta.bg} ${meta.color} border px-2 py-1 text-xs`}>
                    {meta.icon}
                    <span className="ml-1">{meta.label}</span>
                  </Badge>
                  <Badge variant="outline" className="capitalize px-2 py-1 text-xs border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-400">
                    {matchedCategory?.icon && <span className="mr-1">{matchedCategory.icon}</span>}
                    {(item.category || item.category_name || "Unknown")?.toString()}
                  </Badge>

                  {item.type === "aiprompt" && (() => {
                    const level = normalizeAiPromptComplexity(item.complexity);
                    const levelMeta = AI_PROMPT_COMPLEXITY_META[level];
                    return (
                      <Badge
                        asChild
                        variant="outline"
                        className={`px-2 py-1 text-xs font-medium cursor-pointer transition-colors ${levelMeta.badgeClass}`}
                        title={`Complexity: ${levelMeta.label} - click to see other ${levelMeta.label} prompts`}
                      >
                        <Link to={`/ai-prompts?complexity=${level}`} onClick={(e) => e.stopPropagation()}>
                          {levelMeta.label}
                        </Link>
                      </Badge>
                    );
                  })()}

                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 ml-auto">
                    {!hideAuthor && (
                      <>
                        {authorLink ? (
                          <Link
                            to={authorLink}
                            className="font-medium hidden sm:inline hover:underline hover:text-slate-700 dark:hover:text-slate-200"
                          >
                            {authorName}
                          </Link>
                        ) : (
                          <span className="font-medium hidden sm:inline">{authorName}</span>
                        )}
                        <span className="hidden sm:inline">•</span>
                      </>
                    )}
                    <span className="hidden sm:inline">{timeAgo(item.updated_at || item.updatedAt || item.created_at || item.createdAt)}</span>
                    {isOwner && (
                      <>
                        <span className="hidden sm:inline">•</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditOpen(true)}
                          className="h-6 px-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {!isOwner && (
                      <button
                        type="button"
                        onClick={() => setReportTarget({ postType: item.type, postId: Number(item.id) })}
                        aria-label="Report this post"
                        title="Report this post"
                        className="-my-2 -mr-2 grid h-9 w-9 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                      >
                        <Flag className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                )}

                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 sm:hidden">
                  {!hideAuthor && (
                    <>
                      {authorLink ? (
                        <Link to={authorLink} className="font-medium hover:underline">
                          {authorName}
                        </Link>
                      ) : (
                        <span className="font-medium">{authorName}</span>
                      )}
                      <span>•</span>
                    </>
                  )}
                  <span>{timeAgo(item.updated_at || item.updatedAt || item.created_at || item.createdAt)}</span>
                </div>

                <div className="space-y-1">
                {["article", "book", "video"].includes(item.type) ? (
                  <div
                    className="relative"
                    ref={previewAnchorRef}
                    onMouseEnter={handlePreviewEnter}
                    onMouseLeave={handlePreviewLeave}
                    onFocus={handlePreviewEnter}
                    onBlur={handlePreviewLeave}
                  >
                    <a href={item.url} target="_blank" rel="noreferrer" className="block group/link">
                      <h2 className="text-base sm:text-lg font-semibold leading-tight text-slate-900 dark:text-slate-100 group-hover/link:text-slate-700 dark:group-hover/link:text-slate-300 transition-colors duration-200">
                        {displayText}
                        </h2>
                        {domain && (
                          <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                            🔗 {domain}
                          </span>
                        )}
                      </a>

                  </div>
                ) : (
                    <h2
                      className={`font-semibold text-slate-900 dark:text-slate-100 ${
                        isNativeApp && item.type === "quote"
                          ? "border-l-[3px] border-[#fc991d] pl-3 text-[17px] leading-snug"
                          : "text-base sm:text-lg leading-tight"
                      }`}
                    >
                      {displayText}
                      {isLong && (
                        <button onClick={() => setShowFull((prev) => !prev)} className="ml-1 text-blue-500 text-sm hover:underline">
                          {showFull ? " show less" : "...read more"}
                        </button>
                      )}
                    </h2>
                  )}
                </div>

                {/* {item.type === "book" && bookSummaryText && (
                  <motion.div className="flex items-center pt-2">
                    <ImpressiveTooltip
                      label="Try in Perplexity"
                      description="Open the full book summary in Perplexity."
                      icon={<PerplexityIcon className="w-3.5 h-3.5 text-slate-700" />}
                      side="top"
                    >
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={handleBookSummaryClick}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-100 px-5 py-2 text-xs font-semibold text-slate-900 transition duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-400 dark:border-slate-700 dark:bg-gradient-to-r dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 dark:text-slate-100 dark:focus-visible:ring-sky-500"
                        aria-label="Try in Perplexity"
                      >
                        <PerplexityIcon className="w-4 h-4 text-[#0EA5E9]" />
                        <span className="text-[#0F172A] dark:text-slate-100">Try in Perplexity</span>
                        <ArrowUpRight className="w-3 h-3 text-sky-500 dark:text-sky-300" />
                      </motion.button>
                    </ImpressiveTooltip>
                  </motion.div>
                )} */}

                {item.author && item.type === "quote" && (
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                    —{" "}
                    <Link
                      to={`/quotes/author/${slugifyAuthor(item.author)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:underline hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                    >
                      {item.author}
                    </Link>
                  </p>
                )}

                {item.type === "aiprompt" && (
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openInChatGPT}
                      className="flex items-center gap-2 text-xs border-green-300 hover:bg-green-50 hover:border-green-400 dark:border-green-600 dark:hover:bg-green-900 dark:hover:border-green-500"
                    >
                      <ChatGPTLogo className="w-3 h-3" />
                      <span>ChatGPT</span>
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openInPerplexity}
                      className="flex items-center gap-2 text-xs border-blue-300 hover:bg-blue-50 hover:border-blue-400 dark:border-blue-600 dark:hover:bg-blue-900 dark:hover:border-blue-500"
                    >
                      <PerplexityIcon className="w-3 h-3" />
                      <span>Perplexity</span>
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openInClaude}
                      className="flex items-center gap-2 text-xs border-orange-300 hover:bg-orange-50 hover:border-orange-400 dark:border-orange-600 dark:hover:bg-orange-900 dark:hover:border-orange-500"
                    >
                      <ClaudeLogo className="w-3 h-3" />
                      <span>Claude</span>
                    </Button>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-1">
                  {Array.isArray(item.tags) &&
                    item.tags.map((tag, tagIndex) => (
                      <Badge
                        key={`${tag}-${tagIndex}`}
                        variant="secondary"
                        asChild
                        className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer text-xs text-slate-600 dark:text-slate-300"
                      >
                        <Link to={`/tags/${slugify(tag)}`} onClick={(e) => e.stopPropagation()}>
                          #{tag}
                        </Link>
                      </Badge>
                    ))}

                  {isAddingTag && isAuthed ? (
                    <Input
                      autoFocus
                      value={newTagValue}
                      disabled={isSubmittingTag}
                      onChange={(e) => setNewTagValue(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag();
                        } else if (e.key === "Escape") {
                          setIsAddingTag(false);
                          setNewTagValue("");
                        }
                      }}
                      onBlur={() => {
                        if (newTagValue.trim()) handleAddTag();
                        else setIsAddingTag(false);
                      }}
                      placeholder="your tag"
                      className="h-5 w-24 px-1.5 py-0 text-xs"
                    />
                  ) : (
                    <Popover open={communityOpen} onOpenChange={setCommunityOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="Add your own tag. Community tags are listed on hover."
                          onPointerEnter={(e: React.PointerEvent<HTMLElement>) => {
                            if (e.pointerType === "mouse") openCommunityOnHover();
                          }}
                          onPointerLeave={(e: React.PointerEvent<HTMLElement>) => {
                            if (e.pointerType === "mouse") scheduleCommunityClose();
                          }}
                          onPointerDown={(e: React.PointerEvent<HTMLElement>) => {
                            plusPressedByTouch.current = e.pointerType === "touch";
                          }}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (plusPressedByTouch.current) return;
                            setCommunityOpen(false);
                            if (!isAuthed) {
                              onRequireAuth?.();
                              return;
                            }
                            setIsAddingTag(true);
                          }}
                          className={cn(badgeVariants({ variant: "secondary" }), "px-2 py-0.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer text-xs text-slate-500 dark:text-slate-400")}
                        >
                          +
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        align="start"
                        sideOffset={6}
                        collisionPadding={12}
                        onOpenAutoFocus={(e: Event) => e.preventDefault()}
                        onPointerEnter={cancelCommunityClose}
                        onPointerLeave={(e: React.PointerEvent<HTMLElement>) => {
                          if (e.pointerType === "mouse") scheduleCommunityClose();
                        }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        className="w-[min(16rem,calc(100vw-1.5rem))] p-2.5"
                      >
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Community tags{folksonomyTags.length > 0 ? ` (${folksonomyTags.length})` : ""}
                        </p>
                        {folksonomyTags.length === 0 ? (
                          <p className="text-xs text-slate-500 dark:text-slate-400">No community tags yet - add the first one.</p>
                        ) : (
                          <CommunityTagList tags={folksonomyTags} isMine={isMyFolksonomyTag} onRemove={handleRemoveTag} />
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCommunityOpen(false);
                            if (!isAuthed) {
                              onRequireAuth?.();
                              return;
                            }
                            setIsAddingTag(true);
                          }}
                          className="mt-2 inline-block rounded-full bg-slate-900 px-2.5 py-0.5 text-[10px] font-medium leading-4 text-white transition-colors hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                        >
                          {isAuthed ? "Add your tag" : "Log in to add a tag"}
                        </button>
                        <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400">
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-violet-500" /> Yours
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-teal-500" /> Others
                          </span>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>

                {isNativeApp ? (
                  // App footer: the everyday actions as icon + count/label
                  // pills (Copy and Open live in the header's "⋯" menu).
                  <div className="-mx-1.5 flex items-center gap-1 border-t border-slate-100 pt-2 dark:border-slate-700">
                    <button
                      type="button"
                      onClick={handleVote}
                      aria-pressed={Boolean(voted)}
                      aria-label={`${voted ? "Remove like" : "Like"}, ${localPoints} ${localPoints === 1 ? "like" : "likes"}`}
                      className={`${NATIVE_ACTION_CLASS} ${voted ? "bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-400" : ""}`}
                    >
                      <ThumbsUp className={`h-[18px] w-[18px] ${voted ? "fill-current" : ""}`} />
                      <span className="tabular-nums">{localPoints.toLocaleString()}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCommentsOpen(!commentsOpen)}
                      aria-expanded={commentsOpen}
                      aria-label={`Comments, ${item.comments?.length || 0}`}
                      className={`${NATIVE_ACTION_CLASS} ${commentsOpen ? "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white" : ""}`}
                    >
                      <MessageSquare className="h-[18px] w-[18px]" />
                      <span className="tabular-nums">{item.comments?.length || 0}</span>
                    </button>
                    {item.type === "quote" && (
                      <LanguagePicker
                        value={activeLanguageCode}
                        originalLanguage={sourceLanguage}
                        onSelect={handleLanguageChange}
                        align="start"
                      >
                        <button type="button" disabled={translating} className={`${NATIVE_ACTION_CLASS} min-w-0`}>
                          {translating ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Languages className="h-[18px] w-[18px]" />}
                          <span className="max-w-[5.5rem] truncate">
                            {activeLanguageCode === sourceLanguage ? "Translate" : activeLanguage.label}
                          </span>
                        </button>
                      </LanguagePicker>
                    )}
                    {item.type === "article" && item.url && <ArticleListenButton url={item.url} title={item.title} />}
                    {item.id ? (
                      <button type="button" onClick={handleShareItem} className={`${NATIVE_ACTION_CLASS} ml-auto`}>
                        <Share2 className="h-[18px] w-[18px]" />
                        <span>Share</span>
                      </button>
                    ) : null}
                  </div>
                ) : (
                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                  <div className="flex items-center gap-4">
                    <ImpressiveTooltip
                      label={commentsOpen ? "Hide comments" : "comments"}
                      description={commentsOpen ? "Close the discussion thread." : "See how others are reacting."}
                      icon={<MessageSquare className="w-3.5 h-3.5" />}
                      side="top"
                    >
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                        onClick={() => setCommentsOpen(!commentsOpen)}
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span className="text-xs font-medium">{item.comments?.length || 0}</span>
                        {commentsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </motion.button>
                    </ImpressiveTooltip>
                      
                    <ImpressiveTooltip
                      label="Open details"
                      description="Jump into the full conversation view."
                      icon={<Eye className="w-3.5 h-3.5" />}
                      side="top"
                    >
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="text-slate-600 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-400 font-semibold"
                      >
                        <Link to={`/post/${item.type}/${item.id}`}>View</Link>
                      </Button>
                    </ImpressiveTooltip>
                   

                    {item.id ? (
                      <ImpressiveTooltip
                        label="Copy share link"
                        description="Copy a link that opens this item on The Deepam."
                        icon={<Link2 className="w-3.5 h-3.5" />}
                      >
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                          onClick={handleCopyUrl}
                          aria-label="Copy a link to this item"
                        >
                          <Link2 className="w-4 h-4" />
                          <span className="text-xs font-medium hidden sm:inline">Share</span>
                        </motion.button>
                      </ImpressiveTooltip>
                    ) : null}

                    {item.type === "article" && item.url && <ArticleListenButton url={item.url} title={item.title} />}

                    {item.type === "book" && bookSummaryText && (
                      <ImpressiveTooltip
                        label="Try in Perplexity"
                        description="Open this book summary in Perplexity."
                        icon={<PerplexityIcon className="w-3.5 h-3.5 text-slate-700" />}
                        side="top"
                      >
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                          onClick={(event) => handleBookSummaryClick(event)}
                          aria-label="Try in Perplexity"
                        >
                          <PerplexityIcon className="w-4 h-4 text-[#0EA5E9]" />
                          {/* <span className="text-xs font-medium hidden sm:inline">Try in Perplexity</span> */}
                        </motion.button>
                      </ImpressiveTooltip>
                    )}
                    {["aiprompt", "quote"].includes(item.type) && (
                      <ImpressiveTooltip
                        label={item.type === "aiprompt" ? "Copy prompt" : "Copy quote"}
                        description="Send the text straight to your clipboard."
                        icon={<FileText className="w-3.5 h-3.5" />}
                      >
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                          onClick={handleCopyText}
                          aria-label="Copy post text"
                        >
                          <FileText className="w-4 h-4" />
                          <span className="text-xs font-medium hidden sm:inline">Copy Text</span>
                        </motion.button>
                      </ImpressiveTooltip>
                    )}

                    {item.type === "quote" && (
                      <>
                        <LanguagePicker
                          value={activeLanguageCode}
                          originalLanguage={sourceLanguage}
                          onSelect={handleLanguageChange}
                          wrapTrigger={(trigger) => (
                            <ImpressiveTooltip
                              label="Translate"
                              description="View this quote in another language."
                              icon={<Languages className="w-3.5 h-3.5" />}
                            >
                              {trigger}
                            </ImpressiveTooltip>
                          )}
                        >
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors disabled:opacity-60"
                            disabled={translating}
                            aria-label="Translate quote"
                          >
                            {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
                            <span className="text-xs font-medium hidden sm:inline">{activeLanguage.label}</span>
                          </motion.button>
                        </LanguagePicker>

                        {/* <ImpressiveTooltip
                          label={isSpeaking ? "Stop reading" : "Read aloud"}
                          description={isSpeaking ? "Stop the voice reading." : "Listen to this quote read aloud."}
                          icon={<Volume2 className="w-3.5 h-3.5" />}
                        >
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                            onClick={handleReadAloud}
                            aria-label="Read quote aloud"
                          >
                            {isSpeaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                            <span className="text-xs font-medium hidden sm:inline">{isSpeaking ? "Stop" : "Voice"}</span>
                          </motion.button>
                        </ImpressiveTooltip> */}
                      </>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>

            <AnimatePresence>
              {commentsOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700"
                >
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-slate-800 dark:text-slate-200">Comments ({commentsArray.length})</h4>
                    {commentsArray.length === 0 ? (
                      <p className="text-slate-500 dark:text-slate-400 text-center py-4 text-sm">No comments yet. Start the conversation!</p>
                    ) : (
                      <div className="space-y-3">
                        <ScrollArea className="h-[250px] pr-4">
                          <div className="space-y-3">
                            {commentsPreview.map((comment, idx) => {
                              const commenterId = comment?.user_id ?? comment?.userId;
                              const ownsComment =
                                currentUser?.id != null &&
                                commenterId != null &&
                                Number(commenterId) === Number(currentUser.id);
                              const commenterName = comment?.username || comment?.user || "Unknown";
                              const commenterLink =
                                commenterId != null ? `/user/${encodeURIComponent(commenterName)}/${commenterId}` : null;
                              return (
                                <div key={`${comment?.id}-${idx}`} className="flex gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                  <div className="w-7 h-7 rounded-full bg-slate-400 dark:bg-slate-600 flex items-center justify-center text-white font-medium text-xs flex-shrink-0">
                                    {(comment?.username || comment?.user || "NA").slice(0, 2).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      {commenterLink ? (
                                        <Link
                                          to={commenterLink}
                                          className="font-medium text-slate-800 dark:text-slate-200 text-sm hover:underline"
                                        >
                                          {commenterName}
                                        </Link>
                                      ) : (
                                        <span className="font-medium text-slate-800 dark:text-slate-200 text-sm">{commenterName}</span>
                                      )}
                                      <span className="text-slate-400">•</span>
                                      <span className="text-xs text-slate-500 dark:text-slate-400">{timeAgo(comment?.created_at || comment?.createdAt)} ago</span>
                                      {ownsComment && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleDeleteComment(comment.id)}
                                          className="ml-auto p-1 h-auto text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      )}
                                      {!ownsComment && comment?.id != null && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setReportTarget({ postType: item.type, postId: Number(item.id), commentId: Number(comment.id) })
                                          }
                                          aria-label="Report this comment"
                                          title="Report this comment"
                                          className="ml-auto -my-1 grid h-7 w-7 place-items-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-600 dark:hover:text-slate-200"
                                        >
                                          <Flag className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                    <p className="text-slate-700 dark:text-slate-300 text-sm break-words">{comment?.comment || comment?.text || ""}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </ScrollArea>

                        {commentsArray.length > 3 && (
                          <div className="flex justify-center pt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowAllComments(!showAllComments)}
                              className="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 flex items-center gap-1"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                              <span className="text-xs">{showAllComments ? "Show Less" : `Show ${commentsArray.length - 3} more comments`}</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      {currentUser?.picture ? (
                        <img src={currentUser.picture} alt={currentUser.firstName || currentUser.name} className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-slate-500 dark:bg-slate-400 flex items-center justify-center text-white dark:text-slate-900 font-medium text-xs flex-shrink-0">
                          {(currentUser?.username || currentUser?.name || "?").slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 flex gap-2">
                        <Input
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder={isAuthed ? "Add a comment..." : "Log in to comment"}
                          disabled={!isAuthed}
                          className="flex-1 border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400 rounded text-sm h-8"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleAddComment();
                            }
                          }}
                        />
                        <Button
                          onClick={handleAddComment}
                          disabled={!isAuthed || !commentText.trim()}
                          size="sm"
                          className="bg-slate-700 hover:bg-slate-800 dark:bg-slate-300 dark:hover:bg-slate-200 text-white dark:text-slate-900 px-3 rounded h-8 flex-shrink-0"
                        >
                          <Send className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
        {typeof document !== "undefined" && typeof window !== "undefined" && previewAnchorRect && showPreview
          ? createPortal(
              <AnimatePresence>
                {showPreview && (
                  <motion.div
                    key="link-preview"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    style={{
                      position: "absolute",
                      top: previewAnchorRect.bottom + window.scrollY + 8,
                      left: Math.max(8, Math.min(previewAnchorRect.left, window.innerWidth - Math.min(320, previewAnchorRect.width) - 8)),
                      width: Math.min(320, previewAnchorRect.width),
                      minWidth: 220,
                    }}
                    className="p-3 bg-white dark:bg-slate-800 shadow-lg rounded-lg z-50 border border-slate-100 dark:border-slate-700"
                  >
                    {previewLoading && (
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading preview…
                      </div>
                    )}
                    {!previewLoading && preview && (
                      <div className="flex gap-3 items-start">
                        {preview.image && (
                          <img src={preview.image} alt={preview.title} className="w-16 h-16 object-cover rounded-md flex-shrink-0" />
                        )}
                        <div className="space-y-1 min-w-0">
                          <h3 className="font-semibold text-xs text-slate-900 dark:text-slate-100 line-clamp-2">
                            {truncateText(preview.title, 70)}
                          </h3>
                          <p className="text-[11px] text-slate-600 dark:text-slate-400 line-clamp-2">
                            {truncateText(preview.description || preview.summary || "No description available.", 110)}
                          </p>
                        </div>
                      </div>
                    )}
                    {!previewLoading && !preview && previewError && (
                      <p className="text-xs text-red-500">{previewError}</p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>,
              document.body
            )
          : null}

        <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />

        {isOwner && (
          <EditPostModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            item={item}
            onSubmit={onEditPost}
            onDelete={onDeletePost}
            categories={categories}
          />
        )}
      </motion.div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.item === nextProps.item &&
    prevProps.currentUser?.id === nextProps.currentUser?.id &&
    prevProps.globalQuoteLanguage === nextProps.globalQuoteLanguage
);
// End of ItemCard component
const FeedList = ({
  items,
  categories,
  currentUser,
  isLoading,
  onVote,
  onAddComment,
  onDeleteComment,
  onEditPost,
  onDeletePost,
  onRequireAuth,
  hideAuthor = false,
  globalQuoteLanguage,
  loadError = false,
  offline = false,
  onRetry,
}: FeedListProps) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonCard key={index} />
        ))}
      </div>
    );
  }

  if (loadError && !items.length) {
    return (
      <div className="text-center py-12" role="alert">
        <WifiOff className="mx-auto mb-3 h-10 w-10 text-slate-400" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
          {offline ? "You're offline" : "Couldn't load content"}
        </h3>
        <p className="text-slate-600 dark:text-slate-400 text-sm">
          {offline
            ? "Check your connection. The feed reloads when you're back online."
            : "The server didn't respond. Check your connection and try again."}
        </p>
        {onRetry && (
          <Button onClick={onRetry} className="mt-5 rounded-full px-6">
            Try again
          </Button>
        )}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3">🔍</div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">No content found</h3>
        <p className="text-slate-600 dark:text-slate-400 text-sm">Try selecting a different category or content type.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="feed-list">
      {items.map((item, index) => (
        <div key={`${item.id}-${index}`}>
          <ItemCard
            item={item}
            categories={categories}
            index={index}
            currentUser={currentUser}
            onVote={onVote}
            onAddComment={onAddComment}
            onDeleteComment={onDeleteComment}
            onEditPost={onEditPost}
            onDeletePost={onDeletePost}
            onRequireAuth={onRequireAuth}
            hideAuthor={hideAuthor}
            globalQuoteLanguage={globalQuoteLanguage}
          />
        </div>
      ))}
    </div>
  );
};

export default FeedList;
